import React, { useState } from "react";
import { HexColorPicker } from "react-colorful";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { CIRCA_BRAND_DEFAULTS, isBrandHex } from "@/lib/brandColors";
import { cn } from "@/lib/utils";

/**
 * Brand hex field: swatch opens a saturation pad; hex input + clear beside it.
 * Empty value means inherit (preview shows Circa/placeholder default).
 */
export default function BrandColorField({
  id,
  label,
  value = "",
  onChange,
  placeholderKey = "brand_primary",
  placeholder,
  disabled = false,
  className,
}) {
  const circaDefault =
    placeholder || CIRCA_BRAND_DEFAULTS[placeholderKey] || "#E8892D";
  const trimmed = (value || "").trim();
  const valid = isBrandHex(trimmed);
  const preview = valid ? trimmed.toUpperCase() : circaDefault;
  const [open, setOpen] = useState(false);

  return (
    <div className={cn("flex flex-wrap items-center gap-3", className)}>
      <DropdownMenu open={open} onOpenChange={setOpen}>
        <DropdownMenuTrigger asChild disabled={disabled}>
          <button
            type="button"
            aria-label={label ? `${label} color picker` : "Color picker"}
            disabled={disabled}
            className={cn(
              "h-10 w-12 shrink-0 rounded-md border border-input shadow-sm",
              "focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
              "disabled:cursor-not-allowed disabled:opacity-50"
            )}
            style={{ backgroundColor: preview }}
          />
        </DropdownMenuTrigger>
        <DropdownMenuContent
          align="start"
          className="w-auto p-3 space-y-3"
          onCloseAutoFocus={(e) => e.preventDefault()}
        >
          <HexColorPicker
            color={preview}
            onChange={(hex) => onChange?.(hex.toUpperCase())}
            style={{ width: 200, height: 160 }}
          />
          <div className="flex items-center gap-2">
            <Input
              value={valid ? trimmed.toUpperCase() : ""}
              placeholder={circaDefault}
              maxLength={7}
              disabled={disabled}
              className="max-w-[10rem] font-mono uppercase"
              onChange={(e) => onChange?.(e.target.value.toUpperCase())}
              aria-label={label ? `${label} hex` : "Hex color"}
            />
            <Button
              type="button"
              variant="outline"
              size="sm"
              disabled={disabled || !trimmed}
              onClick={() => {
                onChange?.("");
                setOpen(false);
              }}
            >
              Clear
            </Button>
          </div>
        </DropdownMenuContent>
      </DropdownMenu>
      <Input
        id={id}
        value={trimmed}
        placeholder={circaDefault}
        maxLength={7}
        disabled={disabled}
        className="max-w-[10rem] font-mono uppercase"
        onChange={(e) => onChange?.(e.target.value)}
        aria-label={label ? `${label} hex` : "Hex color"}
      />
    </div>
  );
}
